import React, { useState, useRef } from 'react';
import { colors2, DropdownSingle, icons, UI_BODY_SEMIBOLD_SM } from 'oolib';
import TooltipV2 from '@/components/UI/Tooltip';
import { ButtonGhost } from 'oolib/dist/v2/components/Buttons';
import styled from 'styled-components';
import { TextInput } from 'oolib/dist/v2/components/TextInputs';

import { DataFieldRow } from './DataFieldRow';
import { useArithmeticExpression } from './useArithmeticExpression';
import { SearchableLightbox } from '@/components/DataFieldInputSelector/SearchableLightbox';

const { Database, Function: FunctionIcon } = icons;

const StyledSectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

const StyledSectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const StyledAggregateSection = styled.div`
  margin-top: 1rem;
`;

const StyledCustomExpressionWrapper = styled.div`
  margin-top: 0.5rem;
`;

/**
 * ArithmeticExpressionInput - Configure arithmetic expressions on NumberInput fields
 *
 * Thin wrapper around useArithmeticExpression hook — all state + logic lives in the hook.
 * This component only handles rendering. Used by BarChart, LineChart, PieChart.
 * MetricCard uses the hook directly (split across DataSection + ConfigureSection).
 *
 * @param {String} id - Input ID
 * @param {String} label - Label for the input (optional, shown for field A)
 * @param {Array} allOptions - All dimension options (all types)
 * @param {Object} value - Current dimension value (single field or expression object)
 * @param {Function} onChange - Callback when value changes
 * @param {Boolean} disabled - Whether input is disabled
 */
export const ArithmeticExpressionInput = ({
  id,
  label,
  allOptions,
  value,
  onChange,
  disabled,
  picker  // opt-in cascade picker, forwarded to each field row (Sankey step 0)
}) => {
  const {
    // State
    dataFields,
    aggregateType,
    customExpression,
    validationError,

    // Handlers
    handleFieldAChange,
    handleAddField,
    handleFieldChange,
    handleRemoveField,
    handleAggregateChange,
    handleCustomExpressionChange,

    // Computed
    numberInputOptions,
    canAddFields,
    aggregateOptions,
    hasMultipleFields,
    sortedLetters
  } = useArithmeticExpression({ allOptions, value, onChange, disabled });

  const [showAddFieldLightbox, setShowAddFieldLightbox] = useState(false);
  const addBtnRef = useRef(null);

  return (
    <div>
      {/* DATA SECTION */}
      <StyledSectionHeader>
        <StyledSectionTitle>
          <Database size={20} color={colors2.grey60} />
          <UI_BODY_SEMIBOLD_SM style={{ color: colors2.grey80 }}>{label || 'Data'}</UI_BODY_SEMIBOLD_SM>
        </StyledSectionTitle>

        <TooltipV2
          text={
            !dataFields.A
              ? "Add data to visualize"
              : canAddFields
                ? "Add additional number fields to include in this calculation"
                : "Activates only on number fields for arithmetic aggregation"
          }
          placement="top"
        >
          <span ref={addBtnRef}>
            <ButtonGhost
              icon="Plus"
              onClick={() => setShowAddFieldLightbox(true)}
              disabled={!canAddFields || disabled}
            />
          </span>
        </TooltipV2>
        {showAddFieldLightbox && (
          <SearchableLightbox
            anchorRef={addBtnRef}
            onClose={() => setShowAddFieldLightbox(false)}
            options={numberInputOptions}
            grouped={numberInputOptions.some(item => item.options && Array.isArray(item.options))}
            value={undefined}
            onChange={(_, selectedField) => {
              handleAddField(selectedField);
              setShowAddFieldLightbox(false);
            }}
            searchPlaceholder="Search fields"
          />
        )}
      </StyledSectionHeader>

      {/* Field rows */}
      {sortedLetters.map((letter) => (
        <DataFieldRow
          key={letter}
          letter={letter}
          field={dataFields[letter]}
          options={letter === 'A' ? allOptions : numberInputOptions}
          onChange={(f) => letter === 'A' ? handleFieldAChange(f) : handleFieldChange(letter, f)}
          onRemove={letter === 'A' ? null : () => handleRemoveField(letter)}
          disabled={disabled}
          picker={picker}
        />
      ))}

      {/* AGGREGATE SECTION - only show if multiple fields */}
      {hasMultipleFields && (
        <StyledAggregateSection>
          <StyledSectionHeader>
            <StyledSectionTitle>
              <FunctionIcon size={20} color={colors2.grey60} />
              <UI_BODY_SEMIBOLD_SM style={{ color: colors2.grey80 }}>Aggregate</UI_BODY_SEMIBOLD_SM>
            </StyledSectionTitle>
          </StyledSectionHeader>

          <DropdownSingle
            id={`${id}-aggregate`}
            options={aggregateOptions}
            value={aggregateOptions.find(o => o.value === aggregateType)}
            onChange={handleAggregateChange}
            disabled={disabled}
          />

          {/* Custom expression input - auto-applies with debounce */}
          {aggregateType === 'custom' && (
            <StyledCustomExpressionWrapper>
              <TextInput
                S
                id={`${id}-custom-expr`}
                value={customExpression}
                onChange={handleCustomExpressionChange}
                placeholder="e.g., A + B - C"
                validationStatus={validationError ? { type: 'error', msg: validationError } : null}
              />
            </StyledCustomExpressionWrapper>
          )}
        </StyledAggregateSection>
      )}
    </div>
  );
};

export default ArithmeticExpressionInput;
